import React, { useState, useEffect } from 'react';
import { Conta, FormaPagamento, Banco, BaixaPagamentoPayload, TipoContaId, TipoAnexoId } from '../../types/financeiro';
import { EmpresaConfig } from '../../types/empresa';
import { financeiroService } from '../../services/financeiroService';
import { storageService } from '../../services/storageService';
import { empresaService } from '../../services/empresaService';
import { parseContaObs } from '../../utils/financeiroMetadataUtils';
import { sanitizeText } from '../../utils/textSanitizer';
import { usePermissions } from '../../hooks/usePermissions';
import { ContaAnexosManager, PendingAnexo } from './ContaAnexosManager';
import { 
  X, 
  CheckCircle2, 
  Upload, 
  FileText, 
  Trash2, 
  DollarSign, 
  Calendar, 
  AlertCircle, 
  CreditCard,
  RotateCcw,
  Receipt,
  Lock,
  ShieldAlert,
  Landmark,
  Building2,
  Paperclip
} from 'lucide-react';

interface BaixaPagamentoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  conta: Conta | null;
  pessoasMap?: Map<number, string>;
}

export function BaixaPagamentoModal({
  isOpen,
  onClose,
  onSuccess,
  conta,
  pessoasMap = new Map()
}: BaixaPagamentoModalProps) {
  const { hasPermission } = usePermissions();
  const canPay = hasPermission('pay_financial');
  const canViewValues = hasPermission('view_financial_values');
  const [loading, setLoading] = useState(false);
  const [formasPagamento, setFormasPagamento] = useState<FormaPagamento[]>([]);
  const [bancos, setBancos] = useState<Banco[]>([]);
  const [empresas, setEmpresas] = useState<EmpresaConfig[]>([]);
  
  // Form States
  const [dtPagamento, setDtPagamento] = useState(new Date().toISOString().split('T')[0]);
  const [valorPago, setValorPago] = useState<number>(0);
  const [formaPagamentoId, setFormaPagamentoId] = useState<number>(0);
  const [bancoId, setBancoId] = useState<number>(0);
  const [bancoNome, setBancoNome] = useState<string>('');
  const [cnpjFaturador, setCnpjFaturador] = useState<string>('');
  const [numeroNf, setNumeroNf] = useState('');
  const [observacaoBaixa, setObservacaoBaixa] = useState('');
  const [anexos, setAnexos] = useState<PendingAnexo[]>([]);

  useEffect(() => {
    if (isOpen && conta) {
      loadDependencies();
      const meta = parseContaObs(conta.obs, conta.descricao);
      setDtPagamento(conta.dt_pagamento || new Date().toISOString().split('T')[0]);
      setValorPago(conta.valor_pago || conta.valor || 0);
      setFormaPagamentoId(conta.forma_pagamento_id || 0);
      setBancoId(conta.banco_id || meta.banco_id || 0);
      setBancoNome(conta.banco_nome || meta.banco_nome || '');
      setCnpjFaturador(conta.cnpj_faturador || meta.cnpj_faturador || '');
      setNumeroNf(conta.numero_nf || meta.numero_nf || '');
      setObservacaoBaixa('');

      loadAnexos(conta);
    }
  }, [isOpen, conta]);

  async function loadAnexos(c: Conta) {
    try {
      const dbAnexos = await financeiroService.getAnexosConta(c.id);
      const pending: PendingAnexo[] = dbAnexos.map(a => ({
        id: a.id,
        tipo_anexo_id: a.tipo_anexo_id,
        tipo_nome: a.tipo_anexo?.nome || (
          a.tipo_anexo_id === TipoAnexoId.BOLETO ? 'Boleto' :
          a.tipo_anexo_id === TipoAnexoId.NOTA_FISCAL ? 'Nota Fiscal' :
          a.tipo_anexo_id === TipoAnexoId.COMPROVANTE ? 'Comprovante de Pagamento' :
          a.tipo_anexo_id === TipoAnexoId.RECIBO ? 'Recibo' :
          a.tipo_anexo_id === TipoAnexoId.FATURA ? 'Fatura' : 'Outros'
        ),
        descricao: a.descricao,
        arquivo: a.arquivo,
        nome_arquivo: a.descricao,
        isExisting: true
      }));
      setAnexos(pending);
    } catch (err) {
      console.warn('Erro ao carregar anexos para baixa:', err);
    }
  }

  async function loadDependencies() {
    try {
      const [listFormas, listBancos, listEmpresas] = await Promise.all([
        financeiroService.getFormasPagamento(),
        financeiroService.getBancos(),
        empresaService.getEmpresas()
      ]);
      setFormasPagamento(listFormas);
      setBancos(listBancos.filter(b => !b.deleted_at));
      setEmpresas(listEmpresas);
    } catch (err) {
      console.error('Erro ao carregar dados complementares:', err);
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!conta) return;

    if (!dtPagamento) {
      alert('Por favor, informe a data do pagamento.');
      return;
    }

    if (valorPago <= 0) {
      alert('O valor pago deve ser maior que zero.');
      return;
    }

    setLoading(true);
    try {
      const selectedBanco = bancos.find(b => Number(b.id) === Number(bancoId));

      const boletoItem = anexos.find(a => a.tipo_anexo_id === TipoAnexoId.BOLETO);
      const nfItem = anexos.find(a => a.tipo_anexo_id === TipoAnexoId.NOTA_FISCAL);
      const comprovanteItem = anexos.find(a => a.tipo_anexo_id === TipoAnexoId.COMPROVANTE);

      const novosAnexosToSave = anexos
        .filter(a => !a.isExisting && a.arquivo)
        .map(a => ({
          tipo_anexo_id: a.tipo_anexo_id,
          descricao: a.descricao || a.tipo_nome,
          arquivo: a.arquivo
        }));

      const payload: BaixaPagamentoPayload = {
        conta_id: conta.id,
        dt_pagamento: dtPagamento,
        valor_pago: valorPago,
        forma_pagamento_id: formaPagamentoId || undefined,
        banco_id: bancoId || undefined,
        banco_nome: selectedBanco?.nome || bancoNome || undefined,
        cnpj_faturador: cnpjFaturador.trim() || undefined,
        comprovante_url: comprovanteItem?.arquivo || undefined,
        comprovante_nome: comprovanteItem?.descricao || undefined,
        boleto_url: boletoItem?.arquivo || undefined,
        boleto_nome: boletoItem?.descricao || undefined,
        nf_url: nfItem?.arquivo || undefined,
        nf_nome: nfItem?.descricao || undefined,
        novos_anexos: novosAnexosToSave,
        observacao_baixa: observacaoBaixa.trim() || undefined,
        numero_nf: numeroNf.trim() || undefined
      };

      await financeiroService.darBaixaConta(payload);
      onSuccess();
      onClose();
    } catch (err: any) {
      alert('Erro ao processar baixa: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleEstorno = async () => {
    if (!conta) return;
    if (!confirm('Deseja realmente estornar a baixa desta conta? Ela voltará para o status Pendente/Aberta.')) {
      return;
    }

    setLoading(true);
    try {
      await financeiroService.estornarBaixaConta(conta.id);
      onSuccess();
      onClose();
    } catch (err: any) {
      alert('Erro ao estornar baixa: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !conta) return null;

  const isReceita = Number(conta.tipo_conta_id) === TipoContaId.RECEBER;
  const pessoaNome = pessoasMap.get(conta.pessoa_id) || `ID #${conta.pessoa_id}`;
  const isAlreadyPaid = Boolean(conta.dt_pagamento);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 overflow-y-auto">
      <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col border border-slate-200 animate-in fade-in zoom-in duration-200">
        
        {/* Header */}
        <div className={`px-6 py-4 flex items-center justify-between border-b ${
          isReceita ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl ${
              isReceita ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white'
            }`}>
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">
                {isAlreadyPaid ? 'Detalhes da Baixa / Pagamento' : isReceita ? 'Confirmar Recebimento' : 'Dar Baixa no Pagamento'}
              </h3>
              <p className="text-xs text-slate-600">
                Lançamento #{conta.id} • {isReceita ? 'Conta a Receber' : 'Conta a Pagar'}
              </p>
            </div>
          </div>
          <button
            id="btn-close-baixa-modal"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1.5 rounded-lg hover:bg-slate-200/50 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Resumo Card */}
        <div className="p-6 bg-slate-50 border-b border-slate-200 grid grid-cols-2 gap-4">
          <div>
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block">
              {isReceita ? 'Cliente' : 'Fornecedor / Favorecido'}
            </span>
            <p className="text-sm font-bold text-slate-900 truncate mt-0.5" title={sanitizeText(pessoaNome)}>
              {sanitizeText(pessoaNome)}
            </p>
          </div>
          <div>
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block">
              Descrição
            </span>
            <p className="text-sm font-semibold text-slate-800 truncate mt-0.5" title={sanitizeText(conta.descricao) || ''}>
              {sanitizeText(conta.descricao) || 'Sem descrição'}
            </p>
          </div>
          <div>
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block">
              Vencimento Original
            </span>
            <p className="text-sm font-medium text-slate-700 mt-0.5">
              {conta.dt_vencimento ? new Date(conta.dt_vencimento + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}
            </p>
          </div>
          <div>
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block">
              Valor Nominal
            </span>
            <p className="text-base font-extrabold text-slate-900 mt-0.5">
              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(conta.valor)}
            </p>
          </div>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Data do Pagamento */}
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Data do Pagamento / Baixa <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="date"
                  required
                  value={dtPagamento}
                  onChange={(e) => setDtPagamento(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-800 focus:border-red-500 focus:ring-red-500 bg-white"
                />
              </div>
            </div>

            {/* Valor Pago */}
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Valor Efetivamente Pago (R$) <span className="text-red-500">*</span>
              </label>
              {canViewValues ? (
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    required
                    disabled={!canPay}
                    value={valorPago}
                    onChange={(e) => setValorPago(parseFloat(e.target.value) || 0)}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm font-bold text-slate-900 focus:border-red-500 focus:ring-red-500 bg-white"
                  />
                </div>
              ) : (
                <div className="w-full rounded-xl border border-slate-200 bg-slate-100 px-3 py-2 text-sm flex items-center justify-between text-slate-400 font-mono select-none">
                  <span className="flex items-center gap-1.5">
                    <Lock className="w-4 h-4 text-slate-400" />
                    <span>••••••</span>
                  </span>
                  <span className="text-[10px] text-slate-500 font-sans uppercase font-bold">Confidencial</span>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Forma de Pagamento */}
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Forma de Liquidação
              </label>
              <select
                value={formaPagamentoId}
                onChange={(e) => setFormaPagamentoId(Number(e.target.value))}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-red-500 focus:ring-red-500 bg-white font-medium"
              >
                <option value={0}>Selecione a forma...</option>
                {formasPagamento.map((f) => (
                  <option key={f.id} value={f.id}>
                    {sanitizeText(f.nome)}
                  </option>
                ))}
              </select>
            </div>

            {/* Banco / Conta Bancária */}
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Landmark className="w-3.5 h-3.5 text-blue-600" />
                Banco / Conta de Liquidação
              </label>
              <select
                value={bancoId}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  setBancoId(id);
                  const b = bancos.find(x => Number(x.id) === id);
                  if (b?.nome) setBancoNome(b.nome);
                }}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-blue-500 focus:ring-blue-500 bg-white font-medium"
              >
                <option value={0}>Selecione o banco (Ex: Banco do Brasil, BTG)...</option>
                {bancos.map((b) => (
                  <option key={b.id} value={b.id}>
                    {sanitizeText(b.nome)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* CNPJ / Empresa Faturadora */}
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Building2 className="w-3.5 h-3.5 text-indigo-600" />
                Faturado / Liquidado por CNPJ
              </label>
              <input
                type="text"
                placeholder="Ex: CNPJ 1 (Matriz) ou CNPJ 2"
                value={cnpjFaturador}
                onChange={(e) => setCnpjFaturador(e.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-indigo-500 focus:ring-indigo-500 bg-white font-medium"
              />
              <div className="flex flex-wrap gap-1.5 mt-1">
                {empresas.map((emp, idx) => {
                  const label = emp.cnpj && emp.cnpj.length > 5 
                    ? `${emp.cnpj} - ${emp.nome_fantasia || emp.razao_social}` 
                    : emp.nome_fantasia || emp.razao_social || `Empresa ${idx + 1}`;
                  return (
                    <button
                      key={emp.id}
                      type="button"
                      onClick={() => setCnpjFaturador(label)}
                      className="text-[10px] bg-slate-100 hover:bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded cursor-pointer transition-colors"
                    >
                      {emp.nome_fantasia || emp.razao_social}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Nota Fiscal / Documento */}
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Receipt className="w-3.5 h-3.5 text-slate-500" />
                Nº Nota Fiscal / Documento
              </label>
              <input
                type="text"
                placeholder="Ex: NF 10452, Recibo #99"
                value={numeroNf}
                onChange={(e) => setNumeroNf(e.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-red-500 focus:ring-red-500 bg-white font-medium"
              />
            </div>
          </div>

          {/* Anexos: Comprovante, Nota Fiscal, Boleto, Recibos */}
          <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 space-y-2.5">
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <Paperclip className="w-4 h-4 text-slate-500" />
                Documentos & Comprovantes da Baixa
              </span>
              {anexos.length > 0 && (
                <span className="text-[11px] font-bold text-emerald-700 flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> {anexos.length} {anexos.length === 1 ? 'Anexo' : 'Anexos'}
                </span>
              )}
            </label>

            <ContaAnexosManager
              anexos={anexos}
              onChange={setAnexos}
              isReadOnly={isAlreadyPaid}
            />
          </div>

          {/* Observação */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
              Observações da Baixa
            </label>
            <textarea
              rows={2}
              placeholder="Ex: Pago via PIX pelo banco Itaú, chave CNPJ..."
              value={observacaoBaixa}
              onChange={(e) => setObservacaoBaixa(e.target.value)}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-red-500 focus:ring-red-500 bg-white"
            />
          </div>

          {/* Footer Actions */}
          <div className="pt-4 border-t border-slate-200 flex items-center justify-between gap-3">
            {isAlreadyPaid ? (
              <button
                type="button"
                onClick={handleEstorno}
                disabled={loading}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-rose-700 bg-rose-50 border border-rose-200 hover:bg-rose-100 transition-colors cursor-pointer"
              >
                <RotateCcw className="w-4 h-4" />
                Estornar Baixa
              </button>
            ) : (
              <div></div>
            )}

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-xl border border-slate-300 text-xs font-bold text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading || !canPay}
                className={`inline-flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-bold text-white shadow-sm transition-colors cursor-pointer ${
                  isReceita 
                    ? 'bg-emerald-600 hover:bg-emerald-700' 
                    : 'bg-red-600 hover:bg-red-700'
                } disabled:opacity-50`}
              >
                <CheckCircle2 className="w-4 h-4" />
                {loading ? 'Processando...' : isAlreadyPaid ? 'Atualizar Baixa' : 'Confirmar Baixa'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
